import type { SpringOptions } from "motion/react";
import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence } from "motion/react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Lock, ArrowRight, Zap, Shield, Target, Gamepad2, Sparkles, CheckCircle2 } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";

type MediaType = "image" | "gif" | "video";

interface TiltedCardProps {
  imageSrc?: string;
  altText?: string;
  captionText?: string;
  mediaType?: MediaType;
  containerHeight?: React.CSSProperties["height"];
  containerWidth?: React.CSSProperties["width"];
  imageWidth?: React.CSSProperties["width"];
  scaleOnHover?: number;
  rotateAmplitude?: number;
  showMobileWarning?: boolean;
  showTooltip?: boolean;
  startingPrice?: number;
  title?: string;
  description?: string;
  category?: string;
  gameTag?: string;
  tags?: string[];
  pricePlans?: Record<string, number>;
  showFavorite?: boolean;
  onFavoriteClick?: () => void;
  href?: string;
}

const springValues: SpringOptions = {
  damping: 26,
  stiffness: 120,
  mass: 1.2,
};

function getGameTagStyle(tag?: string) {
  const t = (tag || "").toUpperCase();
  if (t.includes("PUBG")) {
    return {
      bg: "bg-amber-500/20 dark:bg-amber-500/20",
      text: "text-amber-600 dark:text-amber-400",
      border: "border-amber-500/30",
    };
  }
  if (t.includes("CS2") || t.includes("CS:GO")) {
    return {
      bg: "bg-sky-500/20 dark:bg-sky-500/20",
      text: "text-sky-600 dark:text-sky-400",
      border: "border-sky-500/30",
    };
  }
  if (t.includes("VALORANT")) {
    return {
      bg: "bg-rose-500/20 dark:bg-rose-500/20",
      text: "text-rose-600 dark:text-rose-400",
      border: "border-rose-500/30",
    };
  }
  return {
    bg: "bg-purple-500/20 dark:bg-purple-500/20",
    text: "text-purple-600 dark:text-purple-400",
    border: "border-purple-500/30",
  };
}

function getCategoryIcon(category?: string) {
  const c = (category || "").toLowerCase();
  if (c.includes("macro")) return Zap;
  if (c.includes("cheat")) return Shield;
  if (c.includes("aimbot")) return Target;
  return Gamepad2;
}

export default function TiltedCard({
  imageSrc,
  altText = "Product",
  captionText = "",
  containerWidth = "100%",
  scaleOnHover = 1.02,
  rotateAmplitude = 2.5,
  startingPrice,
  title,
  description,
  category,
  gameTag,
  tags,
  pricePlans,
  href,
  mediaType = "image",
}: TiltedCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = mediaType === "video";
  const { isMobile } = useMediaQuery();
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useMotionValue(0), springValues);
  const rotateY = useSpring(useMotionValue(0), springValues);
  const scale = useSpring(1, springValues);

  function handleMouse(e: React.MouseEvent<HTMLDivElement>) {
    if (isMobile || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - rect.width / 2;
    const offsetY = e.clientY - rect.top - rect.height / 2;
    rotateX.set((offsetY / (rect.height / 2)) * -rotateAmplitude);
    rotateY.set((offsetX / (rect.width / 2)) * rotateAmplitude);
    x.set(e.clientX - rect.left);
    y.set(e.clientY - rect.top);
  }

  function handleMouseEnter() {
    setIsCardHovered(true);
    if (!isMobile) {
      scale.set(scaleOnHover);
    }
    if (isVideo && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }

  function handleMouseLeave() {
    setIsCardHovered(false);
    if (!isMobile) {
      scale.set(1);
      rotateX.set(0);
      rotateY.set(0);
    }
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
    }
  }

  const planKeys = pricePlans
    ? Object.entries(pricePlans)
        .filter(([_, p]) => typeof p === "number" && p > 0)
        .map(([key]) => key)
    : [];

  const hasMultiplePlans = planKeys.length > 1;

  const displayPrice =
    startingPrice === undefined || startingPrice === null
      ? null
      : hasMultiplePlans
        ? `From $${Number(startingPrice).toFixed(0)}`
        : `$${Number(startingPrice).toFixed(0)}`;

  const priceLabel = hasMultiplePlans ? "Starting at" : "Price";

  const displayTitle = title ?? captionText;
  const CategoryIcon = getCategoryIcon(category);
  const gameTagStyle = getGameTagStyle(gameTag);

  const cardContent = (
    <div
      ref={ref}
      className="group relative flex w-full max-w-[420px] flex-col [perspective:800px]"
      style={{ width: containerWidth }}
      onMouseMove={handleMouse}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className={cn(
          "relative flex size-full flex-col overflow-hidden rounded-[24px] border border-border/70 bg-card text-card-foreground shadow-md transition-all duration-300 [transform-style:preserve-3d]",
          "dark:border-white/10 dark:bg-[#12111d] dark:shadow-2xl dark:shadow-black/50",
          "hover:border-primary/50 hover:shadow-xl dark:hover:border-indigo-500/40 dark:hover:shadow-[0_16px_45px_rgba(99,102,241,0.18)]",
        )}
        style={{
          rotateX: isMobile ? 0 : rotateX,
          rotateY: isMobile ? 0 : rotateY,
          scale: isMobile ? 1 : scale,
        }}
      >
        {/* Top Hero Media Section */}
        <div className="relative h-[220px] sm:h-[235px] w-full shrink-0 overflow-hidden bg-gradient-to-b from-[#1e1a34] to-[#12111d]">
          {/* Subtle grid backdrop for media */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
              backgroundSize: "16px 16px",
            }}
          />

          {/* Media renderer or fallback */}
          {!imgError && imageSrc ? (
            isVideo ? (
              <video
                ref={videoRef}
                src={imageSrc}
                className="block size-full object-cover transition-transform duration-500 group-hover:scale-105"
                muted
                loop
                playsInline
                onError={() => setImgError(true)}
              />
            ) : (
              <Image
                src={imageSrc}
                alt={altText}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                onError={() => setImgError(true)}
              />
            )
          ) : (
            <div className="flex size-full flex-col items-center justify-center p-6 text-center">
              <div className="flex size-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10 text-primary shadow-inner">
                <CategoryIcon className="size-7 stroke-[1.75]" />
              </div>
              <span className="mt-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {gameTag || "Software Solution"}
              </span>
            </div>
          )}

          {/* Top floating badges */}
          <div className="absolute inset-x-3 top-3 z-10 flex items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {gameTag && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider backdrop-blur-md",
                    gameTagStyle.bg,
                    gameTagStyle.text,
                    gameTagStyle.border,
                  )}
                >
                  {gameTag}
                </span>
              )}
              {category && (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/60 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/90 backdrop-blur-md">
                  <CategoryIcon className="size-3" />
                  {category}
                </span>
              )}
            </div>

            {/* Status indicator */}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-black/60 px-2.5 py-0.5 text-[10px] font-bold text-emerald-400 backdrop-blur-md">
              <span className="relative flex size-1.5">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex size-1.5 rounded-full bg-emerald-500" />
              </span>
              Undetected
            </span>
          </div>

          {/* Vignette gradient connecting media to card body */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card via-card/60 to-transparent dark:from-[#12111d] dark:via-[#12111d]/70" />
        </div>

        {/* Bottom Details Section */}
        <div className="flex flex-1 flex-col justify-between p-5 pt-3">
          <div className="flex flex-col gap-2">
            {/* Available Plans Badge List */}
            {hasMultiplePlans ? (
              <div className="flex flex-wrap items-center gap-1">
                {planKeys.map((key) => (
                  <span
                    key={key}
                    className="rounded-md border border-border/50 bg-muted/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground dark:border-white/5 dark:bg-white/5"
                  >
                    {key}
                  </span>
                ))}
              </div>
            ) : tags && tags.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-md border border-border/50 bg-muted/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground dark:border-white/5 dark:bg-white/5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}

            {/* Product Title */}
            {displayTitle && (
              <h3 className="line-clamp-2 text-base font-extrabold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary dark:text-white dark:group-hover:text-indigo-400 sm:text-[17px]">
                {displayTitle}
              </h3>
            )}
          </div>

          {/* Price & Action Row */}
          <div className="mt-4 flex items-end justify-between gap-3 border-t border-border/50 pt-3 dark:border-white/5">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {priceLabel}
              </p>
              {displayPrice !== null && (
                <p className="text-lg font-black tracking-tight text-foreground dark:text-white sm:text-xl">
                  {displayPrice}
                </p>
              )}
            </div>

            {/* Interactive Buy / View CTA Pill */}
            <BuyNowButton isHovered={isCardHovered} />
          </div>
        </div>
      </motion.div>
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="block w-full max-w-[420px]">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

/* ------------------------------------------------------------------ */
/*  BuyNowButton – sleek lock/arrow pill that expands on card hover  */
/* ------------------------------------------------------------------ */

function BuyNowButton({ isHovered }: { isHovered: boolean }) {
  return (
    <motion.span
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-xl font-bold shadow-md transition-colors",
        "bg-primary text-primary-foreground hover:bg-primary/90",
        "dark:bg-white dark:text-black dark:hover:bg-indigo-400 dark:hover:text-white",
      )}
      style={{
        height: 38,
      }}
      animate={{
        width: isHovered ? 116 : 38,
      }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 28,
      }}
    >
      <AnimatePresence mode="wait">
        {isHovered ? (
          <motion.span
            key="buy-text"
            className="flex items-center gap-1.5 px-3 text-[11px] font-black tracking-wider uppercase"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.14 }}
          >
            <span>BUY NOW</span>
            <ArrowRight className="size-3.5" />
          </motion.span>
        ) : (
          <motion.span
            key="lock-icon"
            className="flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.14 }}
          >
            <Lock className="size-4" />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.span>
  );
}

